import { css } from '@emotion/react'
import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import SmileChallengeService from '~/@pages/student/@widgets/SmileChallenge/SmileChallenge.service'

import DailyLearningService from './@service/DailyLearning.service'
import WeeklyHistoryService from './@service/WeeklyHistory.service'
import WeeklyInfoService from './@service/WeeklyInfo.service'
import DailyLearningSection from './@widgets/DailyLearning/DailyLearningSection'
import RecentChallengeSection from './@widgets/RecentChallenge/RecentChallengeSection'
import RecentStudySection from './@widgets/RecentStudy/RecentStudySection'
import SmileChallengeSection from './@widgets/SmileChallenge/SmileChallengeSection'
import WeeklyInfoSection from './@widgets/WeeklyInfo/WeeklyInfoSection'
import WeeklyChart from './@widgets/WeeklySummary/WeeklyChart'
import WeeklyStats from './@widgets/WeeklySummary/WeeklyStats'

const RestrictionHomePage = observer(() => {
  const { isMobile } = useStudentAppMediaQuery()
  const weeklyService = useRepository(WeeklyHistoryService)
  const weeklyInfoService = useRepository(WeeklyInfoService)
  const dailyLearningService = useRepository(DailyLearningService)
  const smileChallengeService = useRepository(SmileChallengeService)
  const trackedRef = useRef(false)
  const studentId = commonRepo.studentId
  const { byFeature, isDailyWorksheetAllowed } = commonRepo.permission
  const isChallengeWorksheetEnabled = byFeature.isAllowed('CHALLENGE_WORKSHEET')
  const isWeeklyHistoryEnabled = byFeature.isAllowed('LEARNING_FOUR_WEEKS_PROBLEM')
  const isSmileChallengeVisible = isChallengeWorksheetEnabled && !isMobile

  useEffect(() => {
    if (!studentId) return
    if (!isWeeklyHistoryEnabled) return
    weeklyService.reset()
    weeklyService.load(studentId).catch((err) => errorHandlerService.handle(err))
  }, [studentId, weeklyService, isWeeklyHistoryEnabled])

  useEffect(() => {
    if (trackedRef.current) return

    const { smileChallenge } = smileChallengeService
    const { weeklyInfo } = weeklyInfoService

    if (!weeklyInfo) return
    if (isSmileChallengeVisible && !smileChallenge) return
    if (isDailyWorksheetAllowed && dailyLearningService.dailyLearning.length === 0) return

    mixpanelClient.track('학습홈_PV', {
      스마일_챌린지_정보_최고등급: !!smileChallenge?.strengths,
      스마일_챌린지_정보_취약유형탈출: !!smileChallenge?.weaknesses,
      이번주_학습정보_학습시간: weeklyInfo?.totalLearningTime ?? 0,
      이번주_학습정보_문제푼개수: weeklyInfo?.problemsSolvedCount ?? 0,
      이번주_학습정보_등급이올라간유형수: weeklyInfo?.bestIncreasedGrade?.length ?? 0,
      이번주_학습정보_최고등급을달성한유형수: weeklyInfo?.bestGradeAchieved?.length ?? 0,
      최대연속학습수행일자: dailyLearningService.maxConsecutiveWeeklyLearningCount,
    })
    trackedRef.current = true
  }, [
    isChallengeWorksheetEnabled,
    isDailyWorksheetAllowed,
    smileChallengeService.smileChallenge,
    weeklyInfoService.weeklyInfo,
    dailyLearningService.dailyLearning,
    dailyLearningService.maxConsecutiveWeeklyLearningCount,
  ])

  const hasLeftSlotAny =
    isDailyWorksheetAllowed || isChallengeWorksheetEnabled || isWeeklyHistoryEnabled

  return (
    <div css={_Style}>
      {hasLeftSlotAny && (
        <div className="slot left">
          {isDailyWorksheetAllowed && <DailyLearningSection />}
          {isChallengeWorksheetEnabled && <WeeklyInfoSection />}
          {isSmileChallengeVisible && !isDailyWorksheetAllowed && <SmileChallengeSection />}
          {isWeeklyHistoryEnabled && (
            <WeeklyStats
              rate={weeklyService.correctRate}
              totalCount={weeklyService.totalProblemCount}
            />
          )}
          {isWeeklyHistoryEnabled && <WeeklyChart data={weeklyService.chartData} />}
        </div>
      )}
      {isChallengeWorksheetEnabled && (
        <div className="slot center">
          {isSmileChallengeVisible && isDailyWorksheetAllowed && <SmileChallengeSection />}
          <RecentChallengeSection />
        </div>
      )}
      <div className="slot right">
        <RecentStudySection />
      </div>
    </div>
  )
})

export default RestrictionHomePage

const _Style = css`
  display: flex;
  height: 100%;
  gap: 24px;

  .slot {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 0;
    min-width: 0;
  }

  .slot.right {
    overflow: hidden;
  }

  ${mediaQuery.underTablet} {
    flex-direction: column;
    gap: 20px;
    padding: 20px 16px 40px;
    overflow-y: auto;
    height: auto;

    .slot {
      flex: none;
      min-height: auto;
    }

    .slot.right {
      overflow: visible;
    }
  }
`
